/* eslint-disable no-console */
import React, { useState } from 'react';
import { types as sdkTypes } from '../../util/sdkLoader';
import EditServiceListingForm from './EditServiceListingForm';

const { Money, UUID } = sdkTypes;

// A saved listing image with the variants the editor page loads
const savedImage = (id, url) => ({
  id: new UUID(id),
  type: 'image',
  attributes: {
    variants: {
      'landscape-crop': { name: 'landscape-crop', width: 400, height: 267, url },
      'landscape-crop2x': { name: 'landscape-crop2x', width: 800, height: 533, url },
    },
  },
});

const imageA = savedImage('img-a', '/static/icons/android-chrome-512x512.png');
const imageB = savedImage('img-b', '/static/logos/freedomly-logo.png');
const imageC = savedImage('img-c', '/static/icons/mstile-150x150.png');
const imageD = savedImage('img-d', '/static/logos/freedomly-wordmark.png');

const description =
  '<p>I help B2B companies plan and produce marketing content that brings in leads. I start with a short workshop, then build a content plan and produce the content with AI-assisted workflows. Every piece is written in the words your buyers search with, and each month you get a short report on what worked and what to change next.</p>';

const hourly = {
  pricingModel: 'hourly',
  price: 12000,
  title: 'Content strategy by the hour',
  description:
    '<p>Flexible senior support for your content work. I help you define audiences, key messages and channel roles.</p>',
};

const project = {
  pricingModel: 'project',
  price: 240000,
  title: 'Content strategy',
  description:
    '<p>A complete content strategy for your brand in four weeks, with a 3-month editorial calendar.</p>',
};

const faq = [
  {
    question: 'How long does delivery take?',
    answer: '<p>Most projects are delivered within two weeks.</p>',
  },
  {
    question: 'How many revision rounds are included?',
    answer: '<p>Two rounds of revisions are included.</p>',
  },
];

// The author's published listings the related pickers offer (Figma 334:1946)
const thumbnail = (id, url) => ({
  id: new UUID(id),
  type: 'image',
  attributes: {
    variants: {
      'scaled-small': { name: 'scaled-small', width: 320, height: 180, url },
      'scaled-medium': { name: 'scaled-medium', width: 750, height: 422, url },
    },
  },
});
const relatedListing = (id, title, publicData, image) => ({
  id: new UUID(id),
  type: 'listing',
  attributes: { title, price: new Money(45000, 'EUR'), publicData },
  images: image ? [thumbnail(`${id}-img`, image)] : [],
});
const relatedWorks = [
  ['Brand identity for a Helsinki café', 'Kahvila Kuura'],
  ['Packaging design for Nordic Tea Co.', 'Nordic Tea Co.'],
  ['Logo refresh for a Tampere bakery', 'Leipomo Kaneli'],
  ['Social media templates for a yoga studio', 'Studio Sisu'],
  ['Annual report layout for a youth charity', ''],
  ['Website illustrations for a software startup', 'Kide Software'],
  ['Event posters for a jazz festival', 'Jazz Kesä'],
  ['Menu design for a ramen bar', 'Ramen Nami'],
].map(([title, client], i) =>
  relatedListing(
    `work-${i + 1}`,
    title,
    { listingType: 'portfolioWork', relatedWorkExperienceCustomer: client },
    i % 3 === 0 ? '/static/icons/android-chrome-512x512.png' : null
  )
);
const relatedServices = [
  ['Logo design for small businesses', 'project'],
  ['Social media graphics on demand', 'hourly'],
  ['Packaging design for food brands', 'project'],
].map(([title, pricingModel], i) =>
  relatedListing(`service-${i + 1}`, title, {
    listingType: 'service',
    packages: [{ pricingModel, price: 45000, title, description: '' }],
  })
);
const relatedLoaded = { status: 'loaded', works: relatedWorks, services: relatedServices };

// Keeps the images in local state, the way the page keeps uploads
const ServiceListingFormExample = props => {
  const [images, setImages] = useState(props.initialValues.images);
  return (
    <EditServiceListingForm
      {...props}
      images={images}
      onImageUpload={({ id, file }) =>
        setImages(prev => [...prev, { id, file, imageId: new UUID(id) }])
      }
      onRemoveImage={id => setImages(prev => prev.filter(image => image.id !== id))}
      onManageDisableScrolling={() => null}
      onSubmit={values => console.log('Submit EditServiceListingForm with values:', values)}
    />
  );
};

// Figma 258:661: a new listing with an open, incomplete package editor
export const NewListingIncomplete = {
  component: ServiceListingFormExample,
  props: {
    listingState: 'new',
    initialValues: {
      title: 'Content Strategy',
      images: [],
      description,
      packages: [hourly],
      faq,
      packageDraft: {
        pricingModel: '',
        price: null,
        title: 'Content strategy',
        description: '<p>A monthly content package with four blog posts.</p>',
        __index: null,
      },
      faqDraft: null,
    },
    relatedOptions: { status: 'loaded', works: [], services: [] },
  },
  group: 'service listing',
};

export const DraftComplete = {
  component: ServiceListingFormExample,
  props: {
    listingState: 'draft',
    initialValues: {
      title: 'Content Strategy',
      images: [imageA, imageB],
      description,
      packages: [hourly, project],
      faq,
      packageDraft: null,
      faqDraft: null,
      relatedWorkIds: ['work-3', 'work-1'],
      relatedServiceIds: [],
    },
    relatedOptions: relatedLoaded,
  },
  group: 'service listing',
};

// An old listing: 4 images and 4 legacy packages must be trimmed before saving
export const PublishedIncomplete = {
  component: ServiceListingFormExample,
  props: {
    listingState: 'published',
    initialValues: {
      title: 'Content Strategy',
      images: [imageA, imageB, imageC, imageD],
      description,
      packages: [
        { pricingModel: 'project', price: 9900, title: '', description: 'Old plain-text package' },
        { ...project, title: 'Basic' },
        { ...project, title: 'Standard', price: 350000 },
        { ...project, title: 'Premium', price: 500000 },
      ],
      faq: [],
      packageDraft: null,
      faqDraft: null,
    },
    relatedOptions: { status: 'error', works: [], services: [] },
  },
  group: 'service listing',
};
